/**
 * Traceability on screen:
 *   <CodeCard>     the record's scannable code in the record rail — the QR (scan URL), the code in
 *                  clear, print labels / tags, the scan history
 *   <TraceChain>   the chain of custody of a lot or an item: origin, where it is now, every movement
 *   <ScanResultCard> what a scanned code resolved to: facts, next steps, the chain
 * All figures come from the server; the QR is drawn by the server too (/api/v1/codes/qr.svg).
 */
import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useQuery } from '../lib/hooks.ts';
import { useI18n } from '../lib/i18n.tsx';
import { useMeta } from '../lib/meta.tsx';
import { useRoute, Link } from '../lib/router.tsx';
import { useSession } from '../lib/session.tsx';
import { Badge, Button, Card, EmptyState, Menu, Spinner, useToast } from './ui.tsx';
import { Icon } from './Icon.tsx';
import type { EntityMeta, Row } from '../lib/types.ts';

export interface TraceMovement { at: string; date: string; type: string; qty: string; unitCost: string; warehouse: string; lot: string | null; document: { entity: string | null; id: string | null; number: string | null }; project: string | null; workOrder: string | null }
export interface TraceChainData {
  subject: { kind: 'lot' | 'item'; id: string; code: string; item: string; itemCode: string };
  origin: { receipt: { id: string; number: string; date: string | null } | null; supplier: string | null; purchaseOrder: string | null; inspection: { id: string; number: string; result: string | null; status: string } | null };
  balances: Array<{ warehouse: string; onHand: string; reserved: string }>;
  movements: TraceMovement[];
}
export interface ScanResult {
  code: string; found: boolean; kind: string; entity: string | null; id: string | null; route: string | null; number: string | null; title: string | null; status: string | null; statusLabel: string | null;
  facts: Array<{ label: string; value: string }>; parent: { entity: string; id: string; route: string; number: string; title: string } | null;
  actions: Array<{ label: string; labelAr: string; path: string; kind: 'open' | 'screen' | 'labels' }>; trace: TraceChainData | null;
}

export const LABEL_ENTITIES = new Set(['goods_receipt', 'goods_receipt_line', 'material_issue', 'stock_transfer', 'production_receipt', 'delivery_note', 'stock_lot', 'item', 'asset', 'inspection', 'ncr']);
export const scanUrlFor = (code: string) => `${window.location.origin}/scan?c=${encodeURIComponent(code)}`;
export const qrSrc = (text: string, module = 4) => api.downloadUrl('codes/qr.svg', { text, module, quiet: 1, inline: 1 });
export const labelsUrl = (entity: string, id: string, size?: string) => api.downloadUrl(`labels/${entity}/${id}`, { format: 'pdf', size, inline: 1 });

export function LabelsMenu({ entity, id, size = 'sm' }: { entity: string; id: string; size?: 'sm' }) {
  const { t } = useI18n();
  const { can } = useSession();
  if (!LABEL_ENTITIES.has(entity) || !can('inventory.label.create')) return null;
  return (
    <Menu align="end" trigger={<Button size={size} icon="Tag" data-print-labels>{t('trace.printLabels')} <Icon name="ChevronDown" size={12} /></Button>} items={[
      { label: t('trace.size50'), icon: 'Tag', onClick: () => window.open(labelsUrl(entity, id, '50x30'), '_blank') },
      { label: t('trace.size100'), icon: 'Tag', onClick: () => window.open(labelsUrl(entity, id, '100x50'), '_blank') },
      { label: t('trace.sizeA4'), icon: 'Grid3x3', onClick: () => window.open(labelsUrl(entity, id, 'a4'), '_blank') },
    ]} />
  );
}

/** the record's code, as a QR and in clear, with printing and the scan history */
export function CodeCard({ def, record }: { def: EntityMeta; record: Row }) {
  const { t, dateTime } = useI18n();
  const toast = useToast();
  const code = def.codeField ? String(record[def.codeField] ?? '') : '';
  const [history, setHistory] = useState(false);
  const scans = useQuery(history ? `scans:${def.name}:${record.id}` : null, () => api.get<Row[]>(`trace/history/${def.name}/${record.id}`), { ttlMs: 0 });
  if (!code) return null;
  return (
    <Card title={<><Icon name="QrCode" size={16} className="muted" /> {t('trace.code')}</>} className="code-card" actions={<LabelsMenu entity={def.name} id={String(record.id)} />}>
      <div className="row" style={{ gap: 14, alignItems: 'flex-start' }} data-code-card>
        <img src={qrSrc(scanUrlFor(code), 3)} alt={code} width={96} height={96} style={{ background: '#fff', borderRadius: 6, border: '1px solid var(--line)' }} title={t('trace.qrHint')} />
        <div className="stack" style={{ gap: 6, minWidth: 0 }}>
          <div className="mono" style={{ fontSize: 15, fontWeight: 700, direction: 'ltr' }} data-code-text>{code}</div>
          <div className="muted" style={{ fontSize: 12 }}>{t('trace.qrHint')}</div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <Button size="sm" icon="Copy" onClick={() => { void navigator.clipboard.writeText(code); toast.info(t('common.copied')); }}>{t('trace.copyCode')}</Button>
            <Button size="sm" icon="History" onClick={() => setHistory((h) => !h)} data-scan-history>{t('trace.scanHistory')}</Button>
          </div>
          {history && (
            <div className="stack" style={{ gap: 4, fontSize: 12 }} data-scan-history-list>
              {!scans.data ? <Spinner /> : scans.data.length === 0 ? <span className="muted">{t('trace.noScans')}</span> : scans.data.slice(0, 8).map((s) => (
                <div key={String(s.id)} className="row" style={{ gap: 8 }}><Icon name="ScanLine" size={12} className="muted" /><span>{t('trace.scannedBy', { user: String(s.user_name ?? '—'), when: dateTime(s.created_at) })}</span>{s.purpose ? <Badge color="gray">{String(s.purpose)}</Badge> : null}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

const TYPE_TONE: Record<string, string> = { receipt: 'green', production_receipt: 'green', transfer_in: 'teal', issue_return: 'green', site_return: 'green', return_to_supplier: 'red', scrap: 'red', production_consumption: 'amber', issue: 'amber', site_delivery: 'blue', transfer_out: 'teal', adjustment: 'gray', count: 'gray' };

/** origin → balances → movements */
export function TraceChain({ data, compact }: { data: TraceChainData; compact?: boolean }) {
  const { t, date, qty } = useI18n();
  const { entities } = useMeta();
  const routeOf = (entity: string | null, id: string | null) => (entity && id && entities.get(entity) ? `${entities.get(entity)!.route}/${id}` : null);
  const typeLabel = (type: string) => { const f = entities.get('stock_movement')?.fields.find((x) => x.name === 'movement_type'); return f?.options?.find((o) => o.value === type)?.label ?? type.replace(/_/g, ' '); };
  const o = data.origin;
  return (
    <div className="stack" style={{ gap: 12 }} data-trace-chain>
      {(o.receipt || o.supplier || o.inspection) && (
        <div className="trace-origin">
          <b>{t('trace.origin')}</b>
          {o.receipt && <span><Icon name="Truck" size={13} /> {t('trace.receipt')}: <Link to={`/procurement/grns/${o.receipt.id}`}>{o.receipt.number}</Link>{o.receipt.date ? ` · ${date(o.receipt.date)}` : ''}</span>}
          {o.supplier && <span><Icon name="Building2" size={13} /> {t('trace.supplier')}: {o.supplier}{o.purchaseOrder ? ` · ${o.purchaseOrder}` : ''}</span>}
          {o.inspection && <span><Icon name="ShieldCheck" size={13} /> {t('trace.inspection')}: <Link to={`/quality/inspections/${o.inspection.id}`}>{o.inspection.number}</Link> <Badge color={o.inspection.result === 'pass' ? 'green' : o.inspection.result === 'fail' ? 'red' : 'amber'}>{o.inspection.result ?? o.inspection.status}</Badge></span>}
        </div>
      )}
      {data.balances.length > 0 && (
        <div className="chips" data-trace-balances>{data.balances.map((b) => <span key={b.warehouse} className="chip"><Icon name="Warehouse" size={12} /> {b.warehouse}: <b>{qty(b.onHand)}</b>{Number(b.reserved) ? ` (${t('trace.reserved')} ${qty(b.reserved)})` : ''}</span>)}</div>
      )}
      {data.movements.length === 0 ? <EmptyState title={t('trace.noMovements')} icon="Route" /> : (
        <div className="table-wrap"><table className="table compact" data-trace-movements>
          <thead><tr><th>{t('trace.date')}</th><th>{t('trace.type')}</th><th>{t('trace.warehouse')}</th><th style={{ textAlign: 'end' }}>{t('trace.qty')}</th><th>{t('trace.document')}</th>{!compact && <th>{t('trace.project')} / {t('trace.workOrder')}</th>}{data.subject.kind === 'item' && <th>{t('trace.kind_lot')}</th>}</tr></thead>
          <tbody>{data.movements.map((m, i) => { const r = routeOf(m.document.entity, m.document.id); return (
            <tr key={i}>
              <td>{date(m.date)}</td>
              <td><Badge color={TYPE_TONE[m.type] ?? 'gray'}>{typeLabel(m.type)}</Badge></td>
              <td>{m.warehouse}</td>
              <td style={{ textAlign: 'end', direction: 'ltr' }} className={Number(m.qty) < 0 ? 'neg' : ''}>{qty(m.qty)}</td>
              <td>{r ? <Link to={r}>{m.document.number ?? m.document.entity}</Link> : m.document.number ?? ''}</td>
              {!compact && <td className="muted">{[m.project, m.workOrder].filter(Boolean).join(' · ')}</td>}
              {data.subject.kind === 'item' && <td className="mono">{m.lot ?? ''}</td>}
            </tr>); })}</tbody>
        </table></div>
      )}
    </div>
  );
}

/** the trace card of a lot or item record page */
export function TraceCard({ def, id }: { def: EntityMeta; id: string }) {
  const { t } = useI18n();
  const kind = def.name === 'stock_lot' ? 'lot' : def.name === 'item' ? 'item' : null;
  const { data, error } = useQuery(kind ? `trace:${kind}:${id}` : null, () => api.get<TraceChainData>(`trace/${kind}/${id}`), { ttlMs: 30_000 });
  if (!kind) return null;
  return <Card title={<><Icon name="Route" size={16} className="muted" /> {t('trace.trace')}</>}>{error ? <span className="muted">{String((error as Error).message)}</span> : !data ? <Spinner /> : <TraceChain data={data} />}</Card>;
}

/** what a code resolved to */
export function ScanResultCard({ result }: { result: ScanResult }) {
  const { t, locale } = useI18n();
  const { navigate } = useRoute();
  if (!result.found) return <div data-scan-result data-scan-kind="unknown" data-scan-code={result.code}><Card><EmptyState icon="ScanLine" title={t('trace.notFound', { code: result.code })} action={result.parent ? <Link to={result.parent.route} className="btn">{t('trace.open')} {result.parent.number}</Link> : undefined} /></Card></div>;
  const kindLabel = t(`trace.kind_${result.kind}`) || result.kind;
  return (
    <div data-scan-result data-scan-kind={result.kind} data-scan-code={result.code}><Card title={<><Badge color="green">{kindLabel}</Badge> <span className="mono">{result.number ?? result.code}</span> {result.statusLabel && <Badge color="teal">{result.statusLabel}</Badge>}</>}
      actions={result.entity && result.id ? <LabelsMenu entity={result.entity} id={result.id} /> : undefined}>
      <div className="stack" style={{ gap: 12 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }} data-scan-title>{result.title}</div>
        {result.parent && <div className="muted">{t('trace.lineOf')} <Link to={result.parent.route}>{result.parent.number} — {result.parent.title}</Link></div>}
        {result.facts.length > 0 && <dl className="facts" data-scan-facts>{result.facts.map((f) => <div key={f.label}><dt>{f.label}</dt><dd>{f.value}</dd></div>)}</dl>}
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }} data-scan-actions>
          {result.actions.map((a) => a.kind === 'labels'
            ? null
            : <Button key={a.path} variant={a.kind === 'open' ? 'primary' : undefined} icon={a.kind === 'open' ? 'ExternalLink' : 'Plus'} onClick={() => navigate(a.path)} data-scan-action={a.kind}>{locale === 'ar' ? a.labelAr : a.label}</Button>)}
        </div>
        {result.trace && <><h4 style={{ margin: '4px 0 0' }}><Icon name="Route" size={14} /> {t('trace.trace')}</h4><TraceChain data={result.trace} compact /></>}
      </div>
    </Card></div>
  );
}
